import { Directive, ElementRef, Input, OnInit, OnDestroy, Renderer2, OnChanges, SimpleChanges } from "@angular/core";

@Directive({
  selector: "[animateIn]",
  standalone: false,
})
export class AnimateInDirective implements OnInit, OnDestroy, OnChanges {
  @Input() animateIn: string = "down"; // Default direction
  @Input() showIf: boolean = true; // Control visibility
  @Input() animateInDuration: number = 100; // Animation duration in ms

  private element: HTMLElement;
  private isAnimating = false;
  private isVisible = true;

  constructor(
    private _elementRef: ElementRef,
    private renderer: Renderer2
  ) {
    this.element = this._elementRef.nativeElement;
  }

  ngOnInit() {
    // Set initial state based on show property
    if (this.showIf) {
      this.animateInElement();
    } else {
      this.hideElement();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Watch for changes to showIf property
    if (changes["showIf"]) {
      const newValue = changes["showIf"].currentValue;
      const previousValue = changes["showIf"].previousValue;

      // Only trigger if the value actually changed
      if (newValue !== previousValue) {
        if (newValue) {
          this.animateInElement();
        } else {
          this.animateOut();
        }
      }
    }
  }

  ngOnDestroy() {
    // Clean up any ongoing animations
    if (this.isAnimating) {
      this.element.style.transition = "";
    }
  }

  private animateInElement() {
    if (this.isAnimating) return;

    this.isAnimating = true;
    this.isVisible = true;
    const direction = this.animateIn.toLowerCase();
    const animationClass = `animate-in-${direction}`;

    // Show element and add animation class
    this.showElement();
    this.element.classList.add(animationClass);

    // Remove animation class after animation completes
    setTimeout(() => {
      this.element.classList.remove(animationClass);
      this.isAnimating = false;
    }, this.animateInDuration);
  }

  private animateOut(): Promise<void> {
    return new Promise((resolve) => {
      if (this.isAnimating) {
        resolve();
        return;
      }

      this.isAnimating = true;
      this.isVisible = false;
      const direction = this.animateIn.toLowerCase();
      const animationClass = `animate-out-${direction}`;

      // Add exit animation class
      this.element.classList.add(animationClass);

      // Hide element after animation completes
      setTimeout(() => {
        this.element.classList.remove(animationClass);
        this.hideElement();
        this.isAnimating = false;
        resolve();
      }, this.animateInDuration);
    });
  }

  private showElement() {
    this.renderer.setStyle(this.element, "display", "block");
    this.renderer.setStyle(this.element, "visibility", "visible");
    this.renderer.setStyle(this.element, "opacity", "1");
  }

  private hideElement() {
    this.renderer.setStyle(this.element, "display", "none");
    this.renderer.setStyle(this.element, "visibility", "hidden");
    this.renderer.setStyle(this.element, "opacity", "0");
  }

  // Public method to trigger exit animation
  public exit(): Promise<void> {
    return this.animateOut();
  }

  // Public method to trigger enter animation
  public enter() {
    this.animateInElement();
  }

  // Public method to check if element is visible
  public get isElementVisible(): boolean {
    return this.isVisible;
  }

  // Public method to check if animation is in progress
  public get isElementAnimating(): boolean {
    return this.isAnimating;
  }
}
